import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { DataHelpers } from '../../DataHelpers';
import { TokenHelper } from '../../TokenHelper';
import { Customer } from '../../interfaces/Customer';
import { customersActionCreators } from '../../store/customers';
import { Consultant } from '../../interfaces/Consultant';
import { ConsultantActionCreators } from '../../store/consultants';
import { dataActionCreators, DataState, TimeSheetEntry } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { Accordion, AccordionHeader, AccordionItem, AccordionItemValue, AccordionPanel, AccordionToggleEventHandler, Button, Divider, Popover, PopoverSurface, PopoverTrigger, Table, TableBody, TableCell, TableCellActions, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Tag, Text } from '@fluentui/react-components';
import { AddIcon, ArrowClockwiseIcon, DownloadIcon, EditIcon, FilterIcon, TrashCanIcon, UploadIcon, useTabStyles } from '../Styles/Editor';
import { ConsultantDropdown } from '../EditorHelpers/ConsultantDropdown';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { DownloadFileAndSave, DownloadSPOLinkAndNav } from '../Tools/FileDownloadHelper';
import { SharePointSVG } from '../Resources/SharePointSVG';
import { useCustomers, useConsultants, useLoggedConsultant, useRigths, useInvoices, useProjects, useProjectsAssigments, useTimeSheetEntries } from '../Tools/ObjectLoader';
import { cutoff } from '../Invoices/InvoicesEditor';

interface TimeSheetsMonth {
    key: AccordionItemValue;
    docs: TimeSheetEntry[];
    year: number;
    month: number;
    MonthTotal: number;
}

interface TSResultSet {
    timeSheetsMonthFilter: TimeSheetsMonth[];
    timeSheetsMonthInvoicedFilter: TimeSheetsMonth[];
    defaultOpenTimeSheetsMonthFilter: AccordionItemValue[];
    defaultOpenTimeSheetsMonthInvoicedFilter: AccordionItemValue[];
}

type TimeSheetsTabProps = {
    ForceLoggedInConsultant: boolean;
};

export const TimeSheetsTab: React.FC<TimeSheetsTabProps> = ({ ForceLoggedInConsultant }) => {
    const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const { customers } = useCustomers();
    const { consultants } = useConsultants();
    const { loggedConsultant } = useLoggedConsultant();
    const { timeSheetEntries } = useTimeSheetEntries();
    const { projects } = useProjects();
    const { projectsAssigments } = useProjectsAssigments();
    const { invoices } = useInvoices();

    const dispatch = useDispatch();
    const { instance } = useMsal();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { t } = useTranslation();
    const [filterConsultant, setFilterConsultant] = React.useState<Consultant | undefined>(undefined);
    const [filterCustomer, setFilterCustomer] = React.useState<Customer | undefined>(undefined);
    const stylesTab = useTabStyles();
    const [defaultOpenTimeSheetsMonthFilter, setdefaultOpenTimeSheetsMonthFilter] = React.useState<AccordionItemValue[]>([]);
    const [defaultOpenTimeSheetsMonthInvoicedFilter, setdefaultOpenTimeSheetsMonthInvoicedFilter] = React.useState<AccordionItemValue[]>([]);
    const [resultSet, setResultSet] = React.useState<TSResultSet>();
    const { isAdmin } = useRigths();
    const viewDebugInfo = useSelector((state: ApplicationState) => state.userNeomyticLogin.viewDebugInfo);
    React.useEffect(() => {
        if (ForceLoggedInConsultant && loggedConsultant) {
            setFilterConsultant(loggedConsultant);
        }
        else {
            setFilterConsultant(undefined);
        }

    }, [ForceLoggedInConsultant, loggedConsultant]);

    React.useEffect(() => {
        if (timeSheetEntries != undefined && projectsAssigments != undefined) {
            let filteredTimesheet = timeSheetEntries;
            // Apply filter if any
            if (filterConsultant) {
                filteredTimesheet = filteredTimesheet
                    .filter(ts => {
                        const pa = projectsAssigments.find(x => x.projectAssignmentid === ts.projectAssignmentid);
                        if (pa && pa.consultantid === filterConsultant.consultantid) {
                            return true;
                        }
                        return false;
                    });
            }
            if (filterCustomer) {
                filteredTimesheet = filteredTimesheet
                    .filter(ts => {
                        const pa = projectsAssigments.find(x => x.projectAssignmentid === ts.projectAssignmentid);
                        if (projects && pa) {
                            const p = projects.find(x => x.projectid === pa.projectid);
                            if (p && p.customerid === filterCustomer.customerid) {
                                return true;
                            }
                        }
                        return false;
                    });
            }

            let LastYear = -1;
            let LastMonth = -1;
            let open = false;
            const TimeSheetsNotInvoiced: TimeSheetsMonth[] = [];
            const DefOpenTimeSheetsNotInvoiced: AccordionItemValue[] = [];
            let TimeSheetsCurrent: TimeSheetsMonth | null = null;
            // split non invoiced
            filteredTimesheet
                .filter(ts => {
                    if (ForceLoggedInConsultant) {
                        const pa = projectsAssigments.find(e => e.projectAssignmentid === ts.projectAssignmentid);
                        return ts.consultantInvoiceid === null && (pa && pa.consultantPrice !== 0) && (ts.date && new Date(ts.date) > cutoff);
                    }
                    return ts.invoiceid === null;
                })
                .forEach((ts) => {
                    const dd = ts.date ? new Date(ts.date) : new Date();
                    if (dd.getFullYear() !== LastYear || dd.getMonth() != LastMonth) {
                        LastYear = dd.getFullYear();
                        LastMonth = dd.getMonth();
                        open = false;
                        if (TimeSheetsCurrent) {
                            TimeSheetsNotInvoiced.push(TimeSheetsCurrent);
                        }
                        TimeSheetsCurrent = {
                            key: "ConsultantInvoices" + (LastMonth + 1) + "/" + LastYear, year: LastYear, month: LastMonth + 1, docs: [], MonthTotal: 0
                        };
                        DefOpenTimeSheetsNotInvoiced.push(TimeSheetsCurrent.key);
                    }

                    const pa = projectsAssigments.find(e => e.projectAssignmentid === ts.projectAssignmentid);
                    const price: number = pa ? ((isAdmin && !ForceLoggedInConsultant) ? pa.customerPrice : pa.consultantPrice) * ts.count : 0;

                    if (TimeSheetsCurrent !== null) {
                        TimeSheetsCurrent.docs.push(ts);
                        TimeSheetsCurrent.MonthTotal += price;
                    }
                });
            if (TimeSheetsCurrent) {
                TimeSheetsNotInvoiced.push(TimeSheetsCurrent);
            }

            // split invoiced
            LastYear = -1;
            LastMonth = -1;
            open = false;
            const TimeSheetsInvoiced: TimeSheetsMonth[] = [];
            const DefOpenTimeSheetsInvoiced: AccordionItemValue[] = [];
            let counter = (isAdmin && !ForceLoggedInConsultant) ? 2 : 12;
            TimeSheetsCurrent = null;
            filteredTimesheet
                .filter(ts => {
                    if (ForceLoggedInConsultant) {
                        const pa = projectsAssigments.find(e => e.projectAssignmentid === ts.projectAssignmentid);
                        return ts.consultantInvoiceid !== null || (pa && pa.consultantPrice === 0) || (ts.date && new Date(ts.date) < cutoff);
                    }
                    return ts.invoiceid !== null;
                })
                .forEach((ts) => {
                    const dd = ts.date ? new Date(ts.date) : new Date();
                    if (dd.getFullYear() !== LastYear || dd.getMonth() != LastMonth) {
                        if ((open || counter > 0) && TimeSheetsCurrent) {
                            counter--;
                            DefOpenTimeSheetsInvoiced.push(TimeSheetsCurrent.key);
                        }
                        LastYear = dd.getFullYear();
                        LastMonth = dd.getMonth();
                        open = false;
                        if (TimeSheetsCurrent) {
                            TimeSheetsInvoiced.push(TimeSheetsCurrent);
                        }
                        TimeSheetsCurrent = {
                            key: "ConsultantInvoices" + (LastMonth + 1) + "/" + LastYear, year: LastYear, month: LastMonth + 1, docs: [], MonthTotal: 0
                        };
                    }

                    const pa = projectsAssigments.find(e => e.projectAssignmentid === ts.projectAssignmentid);
                    const price: number = pa ? ((isAdmin && !ForceLoggedInConsultant) ? pa.customerPrice : pa.consultantPrice) * ts.count : 0;

                    if (TimeSheetsCurrent !== null) {
                        TimeSheetsCurrent.docs.push(ts);
                        TimeSheetsCurrent.MonthTotal += price;
                    }
                });
            if (TimeSheetsCurrent) {
                TimeSheetsInvoiced.push(TimeSheetsCurrent);
            }

            setResultSet({
                timeSheetsMonthFilter: TimeSheetsNotInvoiced,
                defaultOpenTimeSheetsMonthFilter: DefOpenTimeSheetsNotInvoiced,
                timeSheetsMonthInvoicedFilter: TimeSheetsInvoiced,
                defaultOpenTimeSheetsMonthInvoicedFilter: DefOpenTimeSheetsInvoiced,
            });

            setdefaultOpenTimeSheetsMonthFilter(DefOpenTimeSheetsNotInvoiced);
            setdefaultOpenTimeSheetsMonthInvoicedFilter(DefOpenTimeSheetsInvoiced);

        }
    }, [timeSheetEntries, projectsAssigments, filterCustomer, filterConsultant]);

    const itemsHeaders = (isAdmin && !ForceLoggedInConsultant) ?
        [
            { columnKey: "Date", label: "Date", className: stylesTab.width17 },
            { columnKey: "Customer", label: t('Customer'), className: stylesTab.width17 },
            { columnKey: "ProjectProjectsAssigment", label: (t('ProjectsAssigment') + " (" + t('Project') + ")"), className: stylesTab.width17 },
            { columnKey: "Consultant", label: t('Consultant'), className: stylesTab.width17 },
            { columnKey: "Count", label: t('Quantity'), className: stylesTab.width17 },
            { columnKey: "CustomerEuro", label: "€ " + t('Customer'), className: stylesTab.width17 },
        ]
        :
        [
            { columnKey: "Date", label: "Date", className: stylesTab.width17 },
            { columnKey: "Customer", label: t('Customer'), className: stylesTab.width17 },
            { columnKey: "ProjectProjectsAssigment", label: (t('ProjectsAssigment') + " (" + t('Project') + ")"), className: stylesTab.width17 },
            { columnKey: "Count", label: t('Quantity'), className: stylesTab.width17 },
            { columnKey: "CustomerEuro", label: "€ " + t('Consultant'), className: stylesTab.width17 },
        ]
        ;

    const itemsInvoicedHeaders = (isAdmin && !ForceLoggedInConsultant) ?
        [
            { columnKey: "Date", label: "Date", className: stylesTab.width17 },
            { columnKey: "Customer", label: t('Customer'), className: stylesTab.width17 },
            { columnKey: "ProjectProjectsAssigment", label: (t('ProjectsAssigment') + " (" + t('Project') + ")"), className: stylesTab.width17 },
            { columnKey: "Consultant", label: t('Consultant'), className: stylesTab.width17 },
            { columnKey: "Count", label: t('Quantity'), className: stylesTab.width17 },
            { columnKey: "CustomerEuro", label: "€ " + t('Customer'), className: stylesTab.width17 },
            { columnKey: "Invoices", label: t('Invoices'), className: stylesTab.width17 },
        ]
        :
        [
            { columnKey: "Date", label: "Date", className: stylesTab.width17 },
            { columnKey: "Customer", label: t('Customer'), className: stylesTab.width17 },
            { columnKey: "ProjectProjectsAssigment", label: (t('ProjectsAssigment') + " (" + t('Project') + ")"), className: stylesTab.width17 },
            { columnKey: "Count", label: t('Quantity'), className: stylesTab.width17 },
            { columnKey: "CustomerEuro", label: "€ " + t('Consultant'), className: stylesTab.width17 },
        ]
        ;

    const mapTSDocument = (ts: TimeSheetEntry, DisplayInvoiceCol: boolean): JSX.Element => {
        const pa = projectsAssigments ? projectsAssigments.find(e => e.projectAssignmentid === ts.projectAssignmentid) : undefined;
        const p = pa && projects ? projects.find(e => e.projectid === pa.projectid) : undefined;
        const c = p && customers ? customers.find(e => e.customerid === p.customerid) : undefined;
        const co = pa && consultants ? consultants.find(e => e.consultantid === pa.consultantid) : undefined;
        const price: number = pa ? ((isAdmin && !ForceLoggedInConsultant) ? pa.customerPrice : pa.consultantPrice) * ts.count : 0;
        const i = DataHelpers.GetInvoiceFromInvoiceid(data, ts.invoiceid);

        return <TableRow key={ts.timesheetEntryid}>
            <TableCell>
                <TableCellLayout>
                    <Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(dataActionCreators.requestTimesheetQuickView(ts))}
                    >
                        {ts.date ? new Date(ts.date).toLocaleDateString() : "-"}
                    </Button>
                </TableCellLayout>
                <TableCellActions>
                    {ts.timeSheetFileExist && <>
                        <Button
                            icon={<DownloadIcon />}
                            appearance="subtle"
                            aria-label="Download"
                            onClick={() => {
                                TokenHelper.GetToken(instance, teamsState)
                                    .then(token => {
                                        if (token !== null && ts.timesheetEntryid) {
                                            DownloadFileAndSave('api/TimeSheetFile/' + ts.timesheetEntryid, token);
                                        }
                                    });
                            }
                            } />
                        {isAdmin && !ForceLoggedInConsultant &&
                            <Button
                                icon={<SharePointSVG />}
                                appearance="subtle"
                                aria-label={t('ViewInSharePoint')}
                                onClick={() => {
                                    TokenHelper.GetToken(instance, teamsState)
                                        .then(token => {
                                            if (token !== null && ts.timesheetEntryid) {
                                                DownloadSPOLinkAndNav('api/TimeSheets/' + ts.timesheetEntryid + "/DownloadSPOLink", token);
                                            }
                                        });
                                }
                                } />}
                    </>
                    }
                    {((ts.invoiceid === null && ts.consultantInvoiceid === null) || (i !== null && !i.emailSent && !i.peppolSent && i.payment === 0) || viewDebugInfo) && <>
                        <Button
                            icon={<UploadIcon />}
                            appearance="subtle"
                            aria-label="Upload"
                            onClick={() => dispatch(dataActionCreators.requestFileUploadTimeSheet(ts))}
                        />
                        <Button
                            icon={<TrashCanIcon />}
                            appearance="subtle"
                            aria-label="Delete"
                            onClick={() => dispatch(dataActionCreators.requestTimeSheetEntrydeletion(ts))} />
                        <Button
                            icon={<EditIcon />}
                            appearance="subtle"
                            aria-label={t('Edit')}
                            onClick={() => dispatch(dataActionCreators.requestTimeSheetEntryEdition(ts))}
                        />
                    </>
                    }
                </TableCellActions>
            </TableCell>
            <TableCell>
                {c
                    ? <Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(customersActionCreators.requestCustomerQuickView(c))}
                    >
                        {c.displayName ? c.displayName : "?"}
                    </Button>
                    : <Text>?</Text>
                }
            </TableCell>
            <TableCell>
                <TableCellLayout
                    description={p
                        ? <Button appearance="transparent" className={stylesTab.TestButton} size="small"
                            onClick={() => dispatch(dataActionCreators.requestProjectQuickView(p))}
                        >
                            {p.displayName ? "(" + p.displayName.trim() + ")" : "(?)"}
                        </Button>
                        : "(?)"}
                    appearance="primary"
                >
                    {pa
                        ? <Button appearance="transparent" className={stylesTab.TestButton}
                            onClick={() => dispatch(dataActionCreators.requestProjectAssigmentQuickView(pa))}
                        >
                            {pa.displayName ? pa.displayName.trim() : "?"}
                        </Button>
                        : <Text>?</Text>
                    }
                </TableCellLayout>
            </TableCell>
            {isAdmin && !ForceLoggedInConsultant &&
                <TableCell>
                    <Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(ConsultantActionCreators.requestConslutantQuickView(co ? co : null))}
                    >
                        {co ? co.displayName : ""}
                    </Button>
                </TableCell>
            }
            <TableCell>
                <Text>{ts.count + "/" + (pa ? pa.unitOfTime : "?")}</Text>
            </TableCell>
            <TableCell>
                <Text>{price.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
            </TableCell>
            {(isAdmin && !ForceLoggedInConsultant && DisplayInvoiceCol) &&
                <TableCell>
                    <Button appearance="transparent" className={stylesTab.TestButton}
                        onClick={() => dispatch(dataActionCreators.requestInvoiceQuickView(i))}
                    >
                        {i ? i.invoiceRef : "?"}
                    </Button>
                </TableCell>
            }
        </TableRow>;
    };

    const handleToggleDefaultOpenTimeSheetsMonthFilter: AccordionToggleEventHandler<AccordionItemValue> = (event, data) => {
        setdefaultOpenTimeSheetsMonthFilter(data.openItems);
    };
    const handleToggleDefaultOpenTimeSheetsMonthInvoicedFilter: AccordionToggleEventHandler<AccordionItemValue> = (event, data) => {
        setdefaultOpenTimeSheetsMonthInvoicedFilter(data.openItems);
    };

    console.log("Render TimeSheetsTab");

    return <div id="main" className={stylesTab.rootSub}>
        <div className={stylesTab.MainBar}>
            <Popover positioning="below-start">
                <PopoverTrigger>
                    <Button icon={<FilterIcon />} shape="circular" title="Filter" />
                </PopoverTrigger>
                <PopoverSurface>
                    <div className={stylesTab.List}>
                        {!ForceLoggedInConsultant &&
                            <>
                                {t('Consultant')}
                                <ConsultantDropdown
                                    currentConsultant={filterConsultant}
                                    setConsultant={setFilterConsultant}
                                />
                            </>
                        }
                        {t('Customer')}
                        <CustomerDropdown
                            currentCustomer={filterCustomer}
                            customers={customers ?? []}
                            setCustomer={setFilterCustomer} />
                    </div>
                </PopoverSurface>
            </Popover>
            {filterConsultant && !ForceLoggedInConsultant &&
                <Tag
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    size="small"
                    onClick={() => { setFilterConsultant(undefined); }}
                    appearance="outline"
                >
                    {t('Consultant') + " = " + filterConsultant.displayName}
                </Tag>}
            {filterCustomer &&
                <Tag
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    size="small"
                    onClick={() => { setFilterCustomer(undefined); }}
                    appearance="outline"
                >
                    {t('Customer') + " = " + filterCustomer.displayName}
                </Tag>}
            <div className={stylesTab.MarginLeftAuto}>
                <Button icon={<AddIcon />} shape="circular" title={t('Add')}
                    onClick={() => {
                        const pa: TimeSheetEntry = {
                            timesheetEntryid: null,
                            projectAssignmentid: null,
                            date: null,
                            count: 0,
                            invoiceid: null,
                            description: null,
                            timeSheetFileExist: undefined,
                            invoiceDescription: null,
                        };
                        dispatch(dataActionCreators.requestTimeSheetEntryEdition(pa));
                    }} />
                <Button icon={<ArrowClockwiseIcon />} shape="circular" title="Reload"
                    onClick={() => {
                        dispatch(dataActionCreators.ReloadTimesheets());
                    }} />
            </div>

        </div>
        <h2>{t('NotInvoiced2Customer')}</h2>
        <Accordion multiple collapsible openItems={defaultOpenTimeSheetsMonthFilter} onToggle={handleToggleDefaultOpenTimeSheetsMonthFilter}>
            {resultSet && resultSet.timeSheetsMonthFilter.filter((cds) => cds.docs.length > 0).map((cds) => {
                return <AccordionItem key={"AccordionItem" + cds.key} value={cds.key} >
                    <AccordionHeader><Divider appearance="brand">{(cds.month) + "/" + cds.year}</Divider></AccordionHeader>
                    <AccordionPanel>
                        <Table arial-label="Default table">
                            <TableHeader>
                                <TableRow>
                                    {itemsHeaders.map((column) => (
                                        <TableHeaderCell key={column.columnKey} className={column.className}>
                                            {column.label}
                                        </TableHeaderCell>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {cds.docs.map(ts => mapTSDocument(ts, false))}
                            </TableBody>
                        </Table>
                        <div className={stylesTab.FlexCenterGap}>
                            <Text className={stylesTab.InlineCenteredSuccessText}>{"Total " + cds.MonthTotal.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        </div>
                    </AccordionPanel>
                </AccordionItem>;
            })}
        </Accordion>

        <h2>{t('Invoiced2Customer')}</h2>
        <Accordion multiple collapsible openItems={defaultOpenTimeSheetsMonthInvoicedFilter} onToggle={handleToggleDefaultOpenTimeSheetsMonthInvoicedFilter}>
            {resultSet && resultSet.timeSheetsMonthInvoicedFilter.filter((cds) => cds.docs.length > 0).map((cds) => {
                return <AccordionItem key={"AccordionItem" + cds.key} value={cds.key} >
                    <AccordionHeader><Divider appearance="brand">{(cds.month) + "/" + cds.year}</Divider></AccordionHeader>
                    <AccordionPanel>
                        <Table arial-label="Default table">
                            <TableHeader>
                                <TableRow>
                                    {itemsInvoicedHeaders.map((column) => (
                                        <TableHeaderCell key={column.columnKey} className={column.className}>
                                            {column.label}
                                        </TableHeaderCell>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {cds.docs.map(ts => mapTSDocument(ts, true))}
                            </TableBody>
                        </Table>
                        <div className={stylesTab.FlexCenterGap}>
                            <Text className={stylesTab.InlineCenteredSuccessText}>{"Total " + cds.MonthTotal.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                        </div>
                    </AccordionPanel>
                </AccordionItem>;
            })}
        </Accordion>
    </div>
        ;
};